import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { trackEditorialCommerce } from '../../services/editorialRevenueTracking';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faHeart,
  faPaperPlane,
  faBookmark,
  faShoppingBag,
  faExternalLinkAlt,
  faHome,
  faUserCircle,
  faNewspaper
} from '@fortawesome/free-solid-svg-icons';
import editorials from './editorialsData.js';
import './EditorialDetail.css';

const EditorialDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { saveItem, isSaved, user } = useContext(UserContext);
  
  const [editorial, setEditorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featuredEditorials, setFeaturedEditorials] = useState([]);
  const [topics, setTopics] = useState(['All']);
  const [screenSize, setScreenSize] = useState('large');

  // Video references for boomerang effect
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);

  // Boomerang video effect setup
  const setupBoomerangEffect = useCallback((videoElement) => {
    if (!videoElement) return;
    
    let isReversing = false;
    let cleanupFunctions = [];
    
    const handleTimeUpdate = () => {
      if (isReversing) {
        if (videoElement.currentTime <= 0.1) {
          isReversing = false;
          videoElement.currentTime = 0;
          videoElement.play().catch(console.error);
        }
      }
    };
    
    const handleEnded = () => {
      if (!isReversing) {
        isReversing = true;
        videoElement.currentTime = videoElement.duration - 0.1;
        
        const reverseInterval = setInterval(() => {
          if (videoElement.currentTime <= 0.1) {
            clearInterval(reverseInterval);
            isReversing = false;
            videoElement.currentTime = 0;
            videoElement.play().catch(console.error);
          } else {
            videoElement.currentTime -= 0.033;
          }
        }, 33);
        
        cleanupFunctions.push(() => clearInterval(reverseInterval));
      }
    };
    
    const handleCanPlayThrough = () => {
      videoElement.play().catch(console.error);
    };
    
    const handleLoadedData = () => {
      videoElement.playbackRate = 0.6;
      videoElement.style.imageRendering = 'auto';
      videoElement.style.filter = 'contrast(1.05) saturate(1.05) brightness(1.02)';
      videoElement.play().catch(console.error);
    };
    
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
    videoElement.addEventListener('loadeddata', handleLoadedData);
    
    if (videoElement.readyState >= 3) {
      handleLoadedData();
    }
    
    const cleanup = () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      cleanupFunctions.forEach(fn => fn());
    };
    
    cleanupFunctions.push(cleanup);
    return cleanup;
  }, []);

  // Handle responsive screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setScreenSize('small');
      } else if (width < 1050) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Setup videos when refs are available
  useEffect(() => {
    return () => {
      if (leftVideoRef.current) {
        leftVideoRef.current.pause();
      }
      if (rightVideoRef.current) {
        rightVideoRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    // Find the editorial by ID
    const foundEditorial = editorials.find(e => e.id === id);
    
    if (foundEditorial) {
      setEditorial(foundEditorial);
      
      // Track page view
      trackEditorialCommerce(foundEditorial.id, null, 'detail_view', {
        userId: user?.uid || 'anonymous',
        source: 'editorial_detail_page'
      });
    }

    // Set up sidebar data
    const topicSet = new Set(editorials.map(e => e.topic).filter(Boolean));
    setTopics(['All', ...Array.from(topicSet)]);
    
    // Use first 3 editorials if no featured ones exist
    const featuredList = editorials.filter(e => e.featured);
    setFeaturedEditorials(featuredList.length > 0 ? featuredList : editorials.slice(0, 3));
    
    setLoading(false);
  }, [id, user]);

  const handleSave = (e) => {
    e.preventDefault();
    if (saveItem && editorial) {
      saveItem(editorial.id);
      
      trackEditorialCommerce(editorial.id, null, 'save', {
        userId: user?.uid || 'anonymous',
        source: 'editorial_detail_save'
      });
    }
  };

  const handleShare = async () => {
    if (!editorial) return;

    const shareData = {
      title: editorial.title,
      text: editorial.description,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        trackEditorialCommerce(editorial.id, null, 'share', {
          userId: user?.uid || 'anonymous',
          source: 'editorial_detail_share'
        });
      } else {
        await navigator.clipboard.writeText(shareData.url);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
      }
    }
  };

  const handleAffiliateClick = (url, productId = null) => {
    if (editorial && url) {
      window.open(url, '_blank');
      
      trackEditorialCommerce(editorial.id, productId, 'affiliate_click', {
        userId: user?.uid || 'anonymous',
        source: 'editorial_detail_affiliate'
      });
    }
  };

  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: '1.1rem',
        color: '#666'
      }}>
        Loading editorial...
      </div>
    );
  }

  if (!editorial) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#666', marginBottom: '1rem' }}>Editorial Not Found</h2>
        <p style={{ color: '#888', marginBottom: '2rem' }}>
          The editorial you're looking for doesn't exist.
        </p>
        <Link 
          to="/editorials"
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#0d47a1',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '4px'
          }}
        >
          Back to Editorials
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Top navigation menu - show only above 576px width */}
      {windowWidth >= 576 && (
        <div style={{ 
          marginTop: '60px',
          width: '100%', 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'white',
          borderBottom: '1px solid #eee',
          padding: '0.5rem 0'
        }}>
          <div style={{ 
           maxWidth: '750px',
           width: '100%',
           display: 'flex',
           justifyContent: 'space-between',
           alignItems: 'center'
         }}>
           <Link
             to="/discover"
             style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               flexDirection: 'column',
               padding: '0.5rem 1rem',
               background: 'transparent',
               color: '#444',
               textDecoration: 'none',
               borderRadius: '6px',
               minWidth: '80px',
               whiteSpace: 'nowrap',
               overflow: 'hidden'
             }}
           >
             <FontAwesomeIcon
               icon={faHome}
               style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
             />
             <span style={{ fontSize: '0.8rem' }}>Discover</span>
           </Link>

        <Link
          to="/signals"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '0.5rem 1rem',
            background: 'transparent',
            color: '#444',
            textDecoration: 'none',
            borderRadius: '6px',
            minWidth: '80px',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}
        >
          <FontAwesomeIcon
            icon={faShoppingBag}
            style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
          />
          <span style={{ fontSize: '0.8rem' }}>Signals</span>
        </Link>

        <Link
          to="/editorials"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '0.5rem 1rem',
            background: '#0d47a1',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            minWidth: '80px',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}
        >
          <FontAwesomeIcon
            icon={faNewspaper}
            style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
          />
          <span style={{ fontSize: '0.8rem' }}>Editorials</span>
        </Link>
      </div>
    </div>
  )}

  {/* Add spacing when menu is hidden */}
  {windowWidth < 576 && <div style={{ marginTop: '60px' }}></div>}

  {/* Main content container */}
  <div className="editorials-container" style={{ 
    paddingTop: windowWidth >= 576 ? '20px' : '20px'
  }}>
        {/* LEFT SIDEBAR - KEEP VIDEO BACKGROUND, FIX CONTAINER */}
        {windowWidth >= 1300 && (
          <>
            {/* Left Sidebar Video Background - KEEP EXACTLY AS IS */}
            <div style={{
              position: 'fixed',
              left: '0px',
              top: 0,
              width: '210px',
              height: '100vh',
              zIndex: 1,
              overflow: 'hidden'
            }}>
              <video
                ref={leftVideoRef}
                src="/videos/sidebar-column.mov"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                muted
                loop={false}
                playsInline
                preload="metadata"
                autoPlay
                onLoadedData={() => {
                  if (leftVideoRef.current) {
                    setupBoomerangEffect(leftVideoRef.current);
                  }
                }}
                onError={(e) => {
                  console.error('Left video error:', e);
                }}
              />
            </div>
            
            {/* Left Sidebar Content */}
            <div className="editorials-left-sidebar" style={{
              position: 'sticky',
              top: '140px',
              width: '190px',
              height: '650px',
              marginLeft: 'calc(10px - 50vw + 50%)',
              zIndex: 2,
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)'
            }}>
              {/* Content Container */}
              <div style={{
                position: 'relative',
                zIndex: 2,
                background: 'rgba(255, 255, 255, 0.3)',
                marginLeft: '10px',
                marginRight: '10px',
                marginTop: '10px',
                marginBottom: '20px',
                padding: '0.2rem 1rem 1rem',
                borderRadius: '8px',
                minHeight: '605px'
              }}>
                <h3>TOPICS</h3>

{/* Topics List */}
<div style={{ 
  display: 'flex', 
  flexDirection: 'column', 
  gap: '0.5rem',
  maxHeight: '585px',
  overflowY: 'auto',
  marginTop: '0rem',
  marginBottom: '1rem'
}}>
  {topics.map((topic, index) => (
    <button 
      key={index}
      onClick={() => navigate(`/editorials?topic=${topic}`)}
      className=""
    >
      {topic}
    </button>
  ))}
</div>

{/* Scroll Indicator */}
<div style={{
  fontSize: '11px',
  color: '#666',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '0.5rem',
  marginTop: '0.5rem'
}}>
  Scroll for more
</div>
</div>
</div>
</>
)}

   {/* Main editorial container */}
   <div className="editorials-main" style={{
     maxWidth: '600px',
     margin: '0 auto',
     padding: '0 2rem'
   }}>
      {/* Header with back button */}
      <div className="editorial-detail-header">
        <button 
          onClick={() => navigate('/editorials')}
          className="back-button"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Editorials
        </button>
        
        <div className="header-actions">
          <button onClick={handleSave} className="action-button">
            <FontAwesomeIcon
              icon={faBookmark}
              style={{ color: isSaved && isSaved(editorial.id) ? '#000080' : '#666' }}
            />
          </button>

          <button onClick={handleShare} className="action-button">
            <FontAwesomeIcon
              icon={faPaperPlane}
              style={{ color: '#666' }}
            />
          </button>
        </div>
      </div>

      {/* Main content */}
      <article className="editorial-content">
        {/* Topic badge */}
        {editorial.topic && (
          <span className="topic-badge">
            {editorial.topic}
          </span>
        )}

        {/* Title */}
        <h1 style={{
          margin: '0 0 1rem',
          fontSize: '1.8rem',
          fontWeight: '700',
          lineHeight: '1.3',
          color: '#111'
        }}>
          {editorial.title}
        </h1>

        {/* Meta information */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <div>
            <span>By <span style={{ fontWeight: '500' }}>{editorial.author}</span></span>
            <span> | </span>
            <span style={{ fontWeight: '500' }}>{editorial.date}</span>
            {editorial.photographer && (
              <>
                <span> | </span>
                <span>Photography by {editorial.photographer}</span>
              </>
            )}
          </div>
        </div>

        {/* Main image */}
        <div style={{
          position: 'relative',
          width: '800px',
          height: '600px',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
          marginBottom: '1.5rem',
          left: '50%',
          transform: 'translateX(-50%)'
        }}>
          <img 
            src={editorial.image} 
            alt={editorial.title}
            style={{ 
              width: '100%',
              height: '100%', 
              objectFit: 'cover',
              display: 'block'
            }}
          />
        </div>

        {/* Hero image caption */}
        {editorial.imageCaption && (
          <p style={{
            fontSize: '0.85rem',
            color: '#666',
            fontStyle: 'italic',
            margin: '0 0 1.5rem',
            textAlign: 'center',
            lineHeight: '1.4',
            fontWeight: '300'
          }}>
            {editorial.imageCaption}
          </p>
        )}

        {/* Article content */}
        <div className="article-body">
          {/* Description/intro */}
          <div style={{
            margin: '0 0 1.25rem',
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#444'
          }}>
            {editorial.description}
          </div>

          {/* Full content if available */}
          {editorial.content && (
            <div className="article-content">
              {editorial.content.split('\n\n').map((paragraph, index) => {
                // Handle headings
                if (paragraph.startsWith('## ')) {
                  return (
                    <h2 key={index} style={{
                      fontSize: '1.2rem',
                      fontWeight: '600',
                      margin: '1.5rem 0 0.75rem 0',
                      color: '#222'
                    }}>
                      {paragraph.replace('## ', '')}
                    </h2>
                  );
                }
                
                if (paragraph.startsWith('# ')) {
                  return (
                    <h1 key={index} style={{
                      fontSize: '1.4rem',
                      fontWeight: '600',
                      margin: '1.5rem 0 0.75rem 0',
                      color: '#111'
                    }}>
                      {paragraph.replace('# ', '')}
                    </h1>
                  );
                }

                // Handle images
                if (paragraph.includes('<div class="additional-image-container">')) {
                  // Extract image src and caption
                  const srcMatch = paragraph.match(/src="([^"]+)"/);
                  const captionMatch = paragraph.match(/<p class="image-caption">([^<]+)<\/p>/);
                  
                  if (srcMatch && captionMatch) {
                    return (
                      <div key={index} style={{ margin: '1.5rem 0' }}>
                        <img 
                          src={srcMatch[1]} 
                          alt={captionMatch[1]}
                          style={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: '6px',
                            display: 'block'
                          }}
                        />
                        <p style={{
                          fontSize: '0.85rem',
                          color: '#666',
                          fontStyle: 'italic',
                          margin: '0.25rem 0',
                          textAlign: 'center',
                          lineHeight: '1.4',
                          fontWeight: '300'
                        }}>
                          {captionMatch[1]}
                        </p>
                      </div>
                    );
                  }
                }

                // Handle links
                if (paragraph.includes('<a href=')) {
                  return (
                    <p key={index} style={{
                      marginBottom: '1rem',
                      fontWeight: '300',
                      color: '#333',
                      lineHeight: '1.6',
                      fontSize: '1rem'
                    }}>
                      <span dangerouslySetInnerHTML={{ __html: paragraph }} />
                    </p>
                  );
                }

                // Handle regular paragraphs - WITH SIMPLE BOLD PARSING
                if (paragraph.trim()) {
                  // Debug: Log the exact paragraph content
                  if (paragraph.includes('Market Access') || paragraph.includes('Training') || paragraph.includes('Leadership')) {
                    console.log('FOUND PILLARS PARAGRAPH:', JSON.stringify(paragraph));
                    console.log('PARAGRAPH LENGTH:', paragraph.length);
                    console.log('HAS **:', paragraph.includes('**'));
                  }
                  
                  // Simple bold text replacement
                  const processedText = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                  
                  return (
                    <p key={index} style={{
                      marginBottom: '1rem',
                      fontWeight: '300',
                      color: '#333',
                      lineHeight: '1.6',
                      fontSize: '1rem'
                    }}>
                      <span dangerouslySetInnerHTML={{ __html: processedText }} />
                    </p>
                  );
                }

                return null;
              })}
            </div>
          )}

          {/* Additional images if available */}
          {editorial.additionalImages && editorial.additionalImages.length > 0 && (
            <div className="additional-images">
              {editorial.additionalImages.map((img, index) => (
                <div key={index} className="additional-image-container">
                  <img 
                    src={img.src} 
                    alt={img.caption}
                    className="additional-image"
                  />
                  {img.caption && (
                    <p className="image-caption">{img.caption}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Commerce section */}
        {editorial.commerceIntegration && (
          <div className="commerce-section">
            <h3>Related Products</h3>
            
            {/* Affiliate links */}
            {editorial.productMentions && editorial.productMentions.length > 0 && (
              <div className="product-mentions">
                {editorial.productMentions.map((mention, index) => (
                  <button
                    key={index}
                    onClick={() => handleAffiliateClick(mention.affiliateUrl, mention.productId)}
                    className="affiliate-button"
                  >
                    <FontAwesomeIcon icon={faShoppingBag} />
                    Shop {mention.context}
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </button>
                ))}
              </div>
            )}

            {/* General affiliate link */}
            {editorial.affiliateUrl && !editorial.hideShopButton && (
              <button
                onClick={() => handleAffiliateClick(editorial.affiliateUrl, editorial.amazonAsin)}
                className="affiliate-button primary"
              >
                <FontAwesomeIcon icon={faShoppingBag} />
                {editorial.customButtonText || 'Shop on Amazon'}
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </button>
            )}
          </div>
        )}

        {/* Tags */}
        {editorial.tags && editorial.tags.length > 0 && (
          <div className="editorial-tags">
            <h4>Tags</h4>
            <div className="tags-container">
              {editorial.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Affiliate disclosure - hide for specific editorials */}
        {!editorial.hideAffiliateDisclosure && (
          <div className="affiliate-disclosure">
            <p>
              <em>
                This post may contain affiliate links. When you purchase through these links, 
                we may earn a commission at no additional cost to you.
              </em>
            </p>
          </div>
        )}
      </article>

      {/* Related editorials */}
      <div className="related-editorials">
        <h3>More Editorials</h3>
        <div className="related-grid">
          {editorials
            .filter(e => e.id !== editorial.id)
            .slice(0, 3)
            .map(related => (
              <Link 
                key={related.id}
                to={`/editorials/${related.id}`}
                className="related-card"
              >
                <img src={related.image} alt={related.title} />
                <div className="related-content">
                  <h4>{related.title}</h4>
                  <p>{related.description.substring(0, 100)}...</p>
                </div>
              </Link>
            ))}
        </div>
      </div>
     </div>
   
   {/* RIGHT SIDEBAR  */}
   {windowWidth >= 1300 && (
     <>
       {/* Right Sidebar Video Background  */}
       <div style={{
         position: 'fixed',
         right: '0px',
         top: 0,
         width: '210px',
         height: '100vh',
         zIndex: 1,
         overflow: 'hidden'
       }}>
         <video
           ref={rightVideoRef}
           src="/videos/sidebar-column.mov"
           style={{
             width: '100%',
             height: '100%',
             objectFit: 'cover',
             objectPosition: 'center'
           }}
           muted
           loop={false}
           playsInline
           preload="auto"
           autoPlay
           onLoadedData={() => {
             if (rightVideoRef.current) {
               setupBoomerangEffect(rightVideoRef.current);
             }
           }}
           onError={(e) => {
             console.error('Right video error:', e);
           }}
         />
       </div>

       {/* Right Sidebar Content */}
       <div className="editorials-right-sidebar" style={{
         position: 'sticky',
         top: '140px',
         width: '190px',
         height: '650px',
         marginRight: 'calc(10px - 50vw + 50%)',
         zIndex: 2,
         backdropFilter: 'blur(3px)',
         WebkitBackdropFilter: 'blur(3px)'
       }}>
         {/* Featured Editorials Section */}
         <div style={{
           position: 'relative',
           zIndex: 2,
           background: 'rgba(255, 255, 255, 0.3)',
           marginLeft: '10px',
           marginRight: '10px',
           marginTop: '10px',
           marginBottom: '10px',
           padding: '1rem 1rem 1rem',
           borderRadius: '8px',
           minHeight: '450px'
         }}>
           <h3>FEATURED EDITORIALS</h3>

<div style={{ 
  display: 'flex', 
  flexDirection: 'column', 
  gap: '0.5rem',
  maxHeight: '400px',
  overflowY: 'auto',
  marginTop: '0rem',
  marginBottom: '1rem'
}}>
  {featuredEditorials.slice(0, 3).map((editorial) => (
    <button 
      key={editorial.id}
      onClick={() => navigate(`/editorials/${editorial.id}`)}
      className=""
    >
      {editorial.title.length > 30 ? editorial.title.substring(0, 30) + '...' : editorial.title}
    </button>
  ))}
</div>

<div style={{
  fontSize: '11px',
  color: '#666',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '0.5rem',
  marginTop: '0.5rem'
}}>
  Scroll for more
</div>
</div>

         {/* Footer Section */}
<div className="sidebar-footer" style={{
  position: 'relative',
  zIndex: 2,
  background: 'rgba(255, 255, 255, 0.3)',
  borderRadius: '8px',
  padding: '1rem',
  marginLeft: '10px',
  marginRight: '10px',
  marginTop: '10px',
  marginBottom: '20px'
}}>
  <div className="footer-links" style={{
    transform: 'translateX(20px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    fontSize: '0.75rem',
    color: '#666'
  }}>
    <Link to="/about" style={{ color: '#666', textDecoration: 'none' }}>About</Link>
    <Link to="/help" style={{ color: '#666', textDecoration: 'none' }}>Help</Link>
    <Link to="/terms" style={{ color: '#666', textDecoration: 'none' }}>Terms</Link>
    <Link to="/privacy" style={{ color: '#666', textDecoration: 'none' }}>Privacy</Link>
  </div>
  <div className="copyright" style={{
    transform: 'translateX(15px)',
    marginTop: '0.5rem',
    fontSize: '0.7rem',
    color: '#666'
  }}>
    © Artifex {new Date().getFullYear()}
  </div>
</div>
</div>
</>
)}
</div>
</>
  );
};

export default EditorialDetail;